import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, Plus } from 'lucide-react';
import { useSiteData } from '../context/SiteDataContext';
import Reveal from '../components/Reveal';
import PackageCard from '../components/PackageCard';
import VehicleClassPicker from '../components/VehicleClassPicker';
import type { VehicleClass } from '../lib/types';
import { money } from '../lib/format';

export default function Services() {
  const { packages, addons, faqs } = useSiteData();
  const [vehicleClass, setVehicleClass] = useState<VehicleClass>('car');
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <div>
      <section className="relative border-b border-white/[0.06]">
        <div className="hero-grid absolute inset-0" />
        <div className="container-x relative py-16 text-center sm:py-20">
          <Reveal>
            <span className="eyebrow">Detailing Menu</span>
            <h1 className="heading-xl mt-4">Packages & Pricing</h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-zinc-400">
              Every package is priced by vehicle size — select yours below for exact pricing.
              Bigger vehicle, more surface area, same obsessive standard.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <div className="mt-8 flex justify-center">
              <VehicleClassPicker value={vehicleClass} onChange={setVehicleClass} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container-x py-16">
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {packages.map((pkg, i) => (
            <Reveal key={pkg.id} delay={(i % 3) * 100}>
              <PackageCard pkg={pkg} vehicleClass={vehicleClass} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-white/[0.06] bg-night-900/60 py-16">
        <div className="container-x">
          <Reveal>
            <div className="text-center">
              <span className="eyebrow">Enhance Your Detail</span>
              <h2 className="heading-lg mt-3">Add-ons</h2>
              <p className="mx-auto mt-3 max-w-xl text-zinc-400">
                Stack any of these onto a package when you book.
              </p>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {addons.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 80}>
                <div className="card glass-hover flex h-full items-start justify-between gap-4 p-5">
                  <div>
                    <h3 className="flex items-center gap-2 font-display font-semibold text-white">
                      <Plus size={15} className="text-accent-400" /> {a.name}
                    </h3>
                    <p className="mt-1.5 text-sm text-zinc-400">{a.description}</p>
                  </div>
                  <span className="whitespace-nowrap font-display font-bold text-accent-300">
                    {a.fromPrice && <span className="text-xs font-medium text-zinc-500">from </span>}
                    {money(a.price)}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-16">
        <Reveal>
          <div className="text-center">
            <span className="eyebrow">Good To Know</span>
            <h2 className="heading-lg mt-3">Frequently Asked Questions</h2>
          </div>
        </Reveal>
        <div className="mx-auto mt-10 max-w-3xl space-y-3">
          {faqs.map((f) => (
            <Reveal key={f.id}>
              <div className="card overflow-hidden">
                <button
                  className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left font-display font-semibold text-white"
                  onClick={() => setOpenFaq(openFaq === f.id ? null : f.id)}
                >
                  {f.question}
                  <ChevronDown
                    size={18}
                    className={`shrink-0 text-accent-400 transition-transform duration-300 ${openFaq === f.id ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === f.id && (
                  <p className="animate-fade-up px-6 pb-5 text-sm leading-relaxed text-zinc-400">{f.answer}</p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div className="mt-12 text-center">
            <Link to="/booking" className="btn-primary text-base">
              Book Your Detail <ArrowRight size={18} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
